import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ActionsService } from "../../services/ActionsService";
import { getRefetchIntervalMs } from "../../utils/refreshInterval";

export const useActions = (orgId) => {
  return useQuery({
    queryKey: ["actions", orgId],
    queryFn: () => ActionsService.fetchActions(orgId),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useAction = (actionId) => {
  return useQuery({
    queryKey: ["action", actionId],
    queryFn: () => ActionsService.fetchAction(actionId),
    enabled: !!actionId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useActionLogs = (organisationId, actionId, startDate, endDate) => {
  // Action logs should use user refresh rate preference
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: ["actionLogs", organisationId, actionId, startDate, endDate],
    queryFn: () =>
      ActionsService.fetchActionLogs(
        organisationId,
        actionId,
        startDate,
        endDate
      ),
    enabled: !!organisationId && !!actionId,
    staleTime: 1000 * 60 * 2, // 2 minutes for logs
    refetchInterval: refetchIntervalMs,
  });
};

export const useCreateAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ActionsService.createAction,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["actions", variables.orgId] });
    },
  });
};

export const useUpdateAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, data }) =>
      ActionsService.updateAction(actionId, data),
    onSuccess: (_, { actionId, data }) => {
      queryClient.invalidateQueries({ queryKey: ["action", actionId] });
      queryClient.invalidateQueries({ queryKey: ["actions", data.orgId] });
    },
  });
};

export const useDeleteAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ActionsService.deleteAction,
    onSuccess: (_, { actionId, orgId }) => {
      queryClient.invalidateQueries({ queryKey: ["actions", orgId] });
      queryClient.removeQueries({ queryKey: ["action", actionId] });
      queryClient.removeQueries({ queryKey: ["actionLogs", actionId] });
    },
  });
};

export const useExecuteAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ActionsService.executeAction,
    onSuccess: (_, actionId) => {
      queryClient.invalidateQueries({ queryKey: ["actionLogs", actionId] });
    },
  });
};

export const useUpdateActionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, status }) =>
      ActionsService.updateActionStatus(actionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
  });
};
